import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Upload, Info } from 'lucide-react';
import { FilePreview } from './FilePreview';

interface DocumentFileManagerProps {
  documentId?: string; // Optional for new documents
  uploadedFile?: {
    id: string;
    filename: string;
    storage_path: string;
    thumbnail_path: string | null;
    file_type: string;
    file_size: number;
    uploaded_at: string;
  } | null;
  onFileUpload?: (file: File) => Promise<any>;
  isUploading?: boolean;
  isLoading?: boolean;
}

export function DocumentFileManager({
  documentId,
  uploadedFile,
  onFileUpload,
  isUploading = false,
  isLoading = false
}: DocumentFileManagerProps) {
  const [localUploadedFile, setLocalUploadedFile] = React.useState(uploadedFile);

  // Update local state when uploadedFile prop changes
  React.useEffect(() => {
    setLocalUploadedFile(uploadedFile);
  }, [uploadedFile]);

  // Handle file selection and upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && onFileUpload) {
      const file = e.target.files[0];
      try {
        const result = await onFileUpload(file);
        // If the upload was successful and returned file data, update local state
        if (result?.file) {
          setLocalUploadedFile(result.file);
        }
      } catch (error) {
        console.error('File upload failed:', error);
        // Reset the input so user can try again
        e.target.value = '';
      }
    }
  };

  // Handle file removal
  const handleFileRemoved = () => {
    setLocalUploadedFile(null);
  };

  // If we have a file, show the preview
  if (localUploadedFile && documentId) {
    return (
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Upload className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-medium">Document File</h3>
        </div>
        
        <FilePreview
          documentId={documentId}
          file={localUploadedFile}
          onFileRemoved={handleFileRemoved}
        />
      </div>
    );
  }

  // Otherwise, show the upload interface
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Upload className="h-5 w-5 text-muted-foreground" />
        <h3 className="text-lg font-medium">Document File</h3>
      </div>
      
      <div className="space-y-4">
        <div className="grid w-full max-w-sm items-center gap-1.5">
          <Label htmlFor="document-file">Upload Document</Label>
          <Input 
            id="document-file" 
            type="file" 
            onChange={handleFileChange}
            disabled={isUploading || isLoading}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.bmp,.tiff,.tif,.svg"
          />
          <p className="text-sm text-muted-foreground">
            Supported formats: PDF, JPG, PNG, WebP, GIF, BMP, TIFF, SVG (max 50MB)
          </p>
        </div>

        {isUploading && (
          <Alert>
            <Upload className="h-4 w-4" />
            <AlertTitle>Uploading...</AlertTitle>
            <AlertDescription>
              Please wait while your file is being uploaded.
            </AlertDescription>
          </Alert>
        )}

        {localUploadedFile && !documentId && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>File uploaded successfully</AlertTitle>
            <AlertDescription>
              <div className="space-y-1">
                <p><strong>Filename:</strong> {localUploadedFile.filename}</p>
                <p><strong>Size:</strong> {(localUploadedFile.file_size / 1024 / 1024).toFixed(2)} MB</p>
                <p><strong>Type:</strong> {localUploadedFile.file_type}</p>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}
